/**
 * Purchases view — table of purchase events, each with child Items
 * (every saved item becomes a new sealed/card lot at form-save time).
 *
 * Filterable by date range, vendor, and a free-text search across items
 * and notes. Filters drive an Export button that opens the same options
 * modal used by Cards/Sealed inventory.
 */
import { DB } from "../lib/db";
import { slicePage, paginationControlsHtml } from "../utils/pagination";
import { icon } from "../utils/icon";
import { escapeHtml } from "../lib/dom";
import { fmt$, fmt$0, fmtN, fmtDate, sum, kpiBar } from "../utils/format";
import { onSectionChange } from "../state";
import { exportPurchasesReport, openExportOptions } from "../components/printReport";
import { createRowSelection } from "../components/rowSelection";
import type { Row } from "../lib/types";

export const PurchasesFilters = { search: "", vendor: "", from: "", to: "" };
onSectionChange.push((_prev, _next) => { PurchasesFilters.search = ""; });
(window as any).PurchasesFilters = PurchasesFilters;

function applyPurchasesFilters(): Row[] {
  const f = PurchasesFilters;
  let rows = DB.table("purchases").slice().reverse();
  if (f.search) {
    const q = f.search.toLowerCase();
    rows = rows.filter(r => {
      if (Object.values(r).some(v => String(v || "").toLowerCase().includes(q))) return true;
      if (Array.isArray(r.Items) && r.Items.some((it: any) => Object.values(it).some(v => String(v || "").toLowerCase().includes(q)))) return true;
      return false;
    });
  }
  if (f.vendor) rows = rows.filter(r => r["Vendor"] === f.vendor);
  if (f.from)   rows = rows.filter(r => (r["Date"] || "") >= f.from);
  if (f.to)     rows = rows.filter(r => (r["Date"] || "") <= f.to);
  return rows;
}

export function exportPurchasesReportFromView(): void {
  openExportOptions("Purchases", (format, detail) => {
    const rows = applyPurchasesFilters();
    exportPurchasesReport(format, detail, rows, PurchasesFilters);
  });
}
(window as any).exportPurchasesReportFromView = exportPurchasesReportFromView;

const sel = createRowSelection("purchases");
function purchasesEdit(id: string): void { (window as any).editPurchase?.(id); }
function purchasesDelete(id: string): void { (window as any).deleteRow?.("purchases", "Purchase ID", id); }

export function viewPurchases(root: HTMLElement): void {
  const f = PurchasesFilters;
  const purchases = applyPurchasesFilters();
  const totalPaid = sum(purchases, p => p["Total Paid"]);
  const totalFees = sum(purchases, p => p["Fees"]);

  // Distinct vendors actually used in the data for the dropdown.
  const vendors = Array.from(new Set(DB.table("purchases").map(p => p["Vendor"]).filter(Boolean))) as string[];
  vendors.sort();

  const purchases_total = purchases.length;
  const purchases_page = slicePage("purchases", purchases, 50);
  root.innerHTML = `
    ${kpiBar([
      { label: "Purchases",    value: fmtN(purchases.length) },
      { label: "Total Paid",   value: fmt$0(totalPaid) },
      { label: "Fees / Tax",   value: fmt$0(totalFees) },
      { label: "Avg Purchase", value: fmt$0(purchases.length ? totalPaid / purchases.length : 0) },
    ])}

    <div class="panel p-3 mb-3 flex flex-wrap gap-2 items-center">
      <div class="relative flex-1 min-w-[180px]">
        <input class="input w-full pr-8"
               placeholder="Search items, vendor, notes, purchase ID…"
               value="${escapeHtml(f.search)}"
               oninput="PurchasesFilters.search=this.value;render()"
               data-focus-key="purchases-search">
        ${f.search ? `<button type="button" class="absolute right-2 top-1/2 -translate-y-1/2 text-[var(--muted)] hover:text-white text-sm leading-none" onmousedown="event.preventDefault()" onclick="PurchasesFilters.search='';render()" title="Clear search">✕</button>` : ""}
      </div>
      <select class="select !w-44" title="Filter by vendor" onchange="PurchasesFilters.vendor=this.value;render()">
        <option value="">All vendors</option>
        ${vendors.map(o => `<option ${f.vendor === o ? "selected" : ""}>${escapeHtml(o)}</option>`).join("")}
      </select>
      <input class="input !w-36" type="date" title="From date" value="${escapeHtml(f.from)}" oninput="PurchasesFilters.from=this.value;render()">
      <input class="input !w-36" type="date" title="To date" value="${escapeHtml(f.to)}" oninput="PurchasesFilters.to=this.value;render()">
      ${(f.search || f.vendor || f.from || f.to)
        ? `<button class="btn btn-ghost !py-1 !px-2 text-xs" onclick="PurchasesFilters.search='';PurchasesFilters.vendor='';PurchasesFilters.from='';PurchasesFilters.to='';render()">Clear</button>`
        : ""}
      <button class="btn btn-ghost !py-1 !px-2 text-xs" onclick="exportPurchasesReportFromView()" title="Export currently-visible rows as a printable PDF or CSV">🖨 Export</button>
      ${sel.toolbarHtml([
        { id: "edit",   label: `${icon("edit")} Edit`,   title: "Edit selected row", handler: purchasesEdit },
        { id: "delete", label: `${icon("trash")} Delete`, title: "Delete selected row", danger: true, handler: purchasesDelete },
      ])}
    </div>

    <div class="panel scrollx">
      <table class="t">
        <thead><tr>
          <th>Purchase ID</th><th>Date</th><th>Vendor</th><th>Method</th>
          <th>Items</th><th class="text-right">Subtotal</th><th class="text-right">Fees</th>
          <th class="text-right">Total Paid</th><th>Receipt</th><th>Notes</th>
        </tr></thead>
        <tbody>
          ${purchases_page.map(p => {
            const items: any[] = Array.isArray(p.Items) ? p.Items : [];
            const itemQty = items.reduce((s, x) => s + (Number(x.Qty) || 1), 0);
            const summary = items.length === 1
              ? escapeHtml((items[0].Item || "?") + (itemQty > 1 ? ` ×${itemQty}` : ""))
              : items.length
                ? `<b>${items.length}</b> items <span class="text-[var(--muted)]">(${itemQty} units)</span>`
                : `<span class="text-[var(--muted)]">—</span>`;
            const id = escapeHtml(p["Purchase ID"] || "");
            return `<tr ${sel.rowAttrs(id, `editPurchase('${id}')`)}>
              <td class="font-mono text-xs"><a class="text-cyan-300 hover:underline cursor-pointer" onclick="showLotHistory('${id}')">${id}</a></td>
              <td>${escapeHtml(fmtDate(p["Date"]))}</td>
              <td>${escapeHtml(p["Vendor"] || "")}</td>
              <td>${escapeHtml(p["Payment Method"] || "")}</td>
              <td class="text-xs">${summary}</td>
              <td class="text-right stat-num">${fmt$(p["Subtotal"])}</td>
              <td class="text-right stat-num">${fmt$(p["Fees"])}</td>
              <td class="text-right stat-num">${fmt$(p["Total Paid"])}</td>
              <td>${p["Receipt File"]
                  ? `<button class="btn btn-ghost !py-0.5 !px-2 text-xs" onclick="event.stopPropagation(); viewReceiptForPurchase('${id}')" title="${escapeHtml(p["Receipt File Name"] || "View receipt")}">\u{1F4CE} View</button>`
                  : `<span class="text-[var(--muted)] text-xs">\u2014</span>`}</td>
              <td class="text-xs text-[var(--muted)]" title="${escapeHtml(p["Notes"] || "")}">${escapeHtml(p["Notes"] || "")}</td>
            </tr>`;
          }).join("") || `<tr><td colspan="10" class="text-center text-[var(--muted)] py-8">No purchases match your filters.</td></tr>`}
        </tbody>
      </table>
    </div>
    ${paginationControlsHtml("purchases", purchases_total, 50)}
  `;
  sel.applyUI();
}
